import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

// --- Types ---

export interface CampaignOutcome {
  id: string;
  name: string;
  code: string;
  rewardAmount: number | null;
  followUpDays: number | null;
  createsLead: boolean;
  removesFromQueue: boolean;
  orderIndex: number;
}

export interface CampaignOperator {
  id: string;
  userId: string;
  isActive: boolean;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface Campaign {
  id: string;
  name: string;
  description: string | null;
  status: string;
  batchSize: number;
  projectId: string;
  scriptContent: string | null;
  project?: { id: string; name: string };
  outcomes?: CampaignOutcome[];
  operators?: CampaignOperator[];
  _count?: { contacts: number; operators: number };
  _stats?: {
    pending: number;
    assigned: number;
    in_progress: number;
    completed: number;
    callback: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CampaignQueueContact {
  id: string;
  status: string;
  callCount: number;
  callbackAt: string | null;
  notes: string | null;
  contact: {
    id: string;
    firstName: string;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    personalEmail: string | null;
    personalPhone: string | null;
    position: string | null;
    department: string | null;
    notes: string | null;
    temperature: string | null;
    tags: string[];
    linkedinUrl: string | null;
    websiteUrl: string | null;
    meta?: Record<string, unknown>;
    company?: {
      id: string;
      name: string;
      ico: string | null;
      phone: string | null;
      website: string | null;
    } | null;
    leads?: Array<{
      id: string;
      leadType: string;
      status: string;
      notes: string | null;
      expectedValue: number | null;
      createdAt: string;
      updatedAt: string;
      assignedTo: string | null;
      currentStage?: { id: string; name: string; color: string } | null;
      project?: { id: string; name: string } | null;
    }>;
    activities?: Array<{
      id: string;
      type: string;
      subject: string | null;
      body: string | null;
      meta: Record<string, unknown> | null;
      completedAt: string | null;
      createdAt: string;
    }>;
  };
  callLogs: Array<{
    id: string;
    duration: number;
    notes: string | null;
    createdAt: string;
    outcome: { id: string; name: string; code: string };
  }>;
}

export interface CampaignStats {
  total: number;
  pending: number;
  assigned: number;
  in_progress: number;
  completed: number;
  callback: number;
  outcomes: Array<{
    outcomeId: string;
    name: string;
    code: string;
    count: number;
    totalRewards: number;
  }>;
}

export interface CampaignOperatorStats {
  operators: Array<{
    userId: string;
    fullName: string;
    firstName: string;
    lastName: string;
    calls: number;
    totalDuration: number;
    contactsCompleted: number;
    totalRewards: number;
  }>;
}

export interface CreateCampaignDto {
  name: string;
  description?: string;
  projectId: string;
  batchSize?: number;
  scriptContent?: string;
  outcomes?: Array<{
    name: string;
    code: string;
    rewardAmount?: number;
    followUpDays?: number;
    createsLead?: boolean;
    removesFromQueue?: boolean;
  }>;
}

export interface UpdateCampaignDto {
  name?: string;
  description?: string;
  status?: string;
  batchSize?: number;
  scriptContent?: string;
  outcomes?: Array<{
    name: string;
    code: string;
    rewardAmount?: number;
    followUpDays?: number;
    createsLead?: boolean;
    removesFromQueue?: boolean;
  }>;
  operatorIds?: string[];
}

// --- Hooks ---

export function useCampaigns() {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: ['campaigns', currentTenant?.id],
    queryFn: () => api.get<Campaign[]>('/campaigns'),
    enabled: !!currentTenant,
  });
}

export function useCampaign(id: string) {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: ['campaign', id, currentTenant?.id],
    queryFn: () => api.get<Campaign>(`/campaigns/${id}`),
    enabled: !!id && !!currentTenant,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCampaignDto) => api.post<Campaign>('/campaigns', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignDto }) =>
      api.patch<Campaign>(`/campaigns/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
  });
}

export function usePopulateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post(`/campaigns/${id}/populate`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
  });
}

export function useAssignBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, userId }: { campaignId: string; userId?: string }) =>
      api.post(`/campaigns/${campaignId}/assign`, userId ? { userId } : undefined),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
    },
  });
}

export function useCampaignQueue(id: string) {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: ['campaign-queue', id, currentTenant?.id],
    queryFn: () => api.get<CampaignQueueContact[]>(`/campaigns/${id}/queue`),
    enabled: !!id && !!currentTenant,
  });
}

export function useTakeBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) => api.post(`/campaigns/${campaignId}/take-batch`),
    onSuccess: (_, campaignId) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-queue', campaignId] });
    },
  });
}

export function useLogCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      contactId,
      data,
    }: {
      campaignId: string;
      contactId: string;
      data: {
        outcomeId: string;
        duration: number;
        notes?: string;
        callbackAt?: string;
      };
    }) => api.post(`/campaigns/${campaignId}/contacts/${contactId}/call`, data),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-queue', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
    },
  });
}

export function useCampaignStats(id: string, enabled = true) {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: ['campaign-stats', id, currentTenant?.id],
    queryFn: () => api.get<CampaignStats>(`/campaigns/${id}/stats`),
    enabled: !!id && !!currentTenant && enabled,
  });
}

export function useCampaignOperatorStats(id: string, enabled = true) {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: ['campaign-operator-stats', id, currentTenant?.id],
    queryFn: () => api.get<CampaignOperatorStats>(`/campaigns/${id}/stats/operators`),
    enabled: !!id && !!currentTenant && enabled,
  });
}

// ── Rewards ──

export function usePendingRewards(campaignId: string, enabled = true) {
  const { currentTenant } = useTenant();
  return useQuery({
    queryKey: ['campaign-rewards-pending', campaignId, currentTenant?.id],
    queryFn: () => api.get<any[]>(`/campaigns/${campaignId}/rewards/pending`),
    enabled: !!campaignId && !!currentTenant && enabled,
  });
}

export function useRewardsSummary(campaignId: string, enabled = true) {
  const { currentTenant } = useTenant();
  return useQuery({
    queryKey: ['campaign-rewards-summary', campaignId, currentTenant?.id],
    queryFn: () => api.get<{ pending: { count: number; total: number }; approved: { count: number; total: number }; rejected: { count: number; total: number } }>(`/campaigns/${campaignId}/rewards/summary`),
    enabled: !!campaignId && !!currentTenant && enabled,
  });
}

export function useApproveRewards() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, callLogIds }: { campaignId: string; callLogIds: string[] }) =>
      api.post(`/campaigns/${campaignId}/rewards/approve`, { callLogIds }),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-rewards-pending', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-rewards-summary', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-stats', campaignId] });
    },
  });
}

export function useRejectRewards() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, callLogIds, note }: { campaignId: string; callLogIds: string[]; note?: string }) =>
      api.post(`/campaigns/${campaignId}/rewards/reject`, { callLogIds, note }),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-rewards-pending', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-rewards-summary', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-stats', campaignId] });
    },
  });
}
